import { useState } from 'react';
import { Edit05, Send01, Trash01 } from '@kula-ai/icons-core';
import { Flex } from '@chakra-ui/react';
import {
  ActivityBusyIcon,
  ActivityMoreMenu,
  ActivityTooltip,
} from '@/components/candidateActivities/kit';
import {
  useCancelInternalEmail,
  useSendInternalEmailNow,
} from '@/api/internalEmails';
import { useShowToast } from '@/hooks';

const SEND_NOW_LABEL = 'Send now';
// While the send is in flight the item says so and refuses to run again: the menu
// closes on click, so nothing else told the sender it had already started — and
// reopening it and clicking again sent the email twice.
const SENDING_LABEL = 'Sending';
const EDIT_LABEL = 'Edit email';
const DELETE_LABEL = 'Delete email';
const DELETING_LABEL = 'Deleting email';
const SEND_NOW_FAILED = 'Sending the internal email failed';
const SEND_NOW_SUCCEEDED = 'Internal email sending in progress';
const DELETE_FAILED = 'Failed to delete email';
const DELETE_SUCCEEDED = 'Email deleted successfully';
// The pair Figma 18313:17620 gives this menu, same as the Overview row's.
const EDIT_COLOR = 'primary-500';
const DELETE_COLOR = 'status-1';

interface InternalEmailScheduledActionsProps {
  internalEmailId: number | string;
  /**
   * Opens the internal email composer on this email.
   *
   * The composer is not imported here on purpose: this tab is reachable from
   * `SendInternalEmailDrawer`'s own transitive imports, so importing it back
   * would close an import cycle (`scripts/circular-deps.mjs`). The tab's parent
   * owns the drawer and hands the trigger down. Without one there is nothing to
   * open, so the item is left out rather than shown dead.
   */
  onEdit?: () => void;
  /** Re-read the scheduled list once the email has changed. */
  onDone?: () => void;
}

/**
 * The Scheduled row's actions for an internal email.
 *
 * An internal email is an `AtsUserMessage`; every other row in this section is
 * an `AtsCandidateMessage`, and the tab's handlers (`useEmailActions`, plus the
 * `email_activity` lookup behind Edit) address candidate messages by id. Handed
 * an internal email's id they act on whichever candidate message happens to
 * carry that number — which is how Edit opened an empty candidate composer,
 * with a candidate template in the body and no sender.
 *
 * Same three actions the row already offered, routed to the internal email's own
 * endpoints and to the composer that knows how to edit one. Mirrors
 * `InternalEmailRowActions` on the Overview tab, so the email behaves the same
 * wherever it is read.
 */
export const InternalEmailScheduledActions = ({
  internalEmailId,
  onEdit,
  onDone = () => {},
}: InternalEmailScheduledActionsProps) => {
  const toast = useShowToast();
  const { mutateAsync: cancelInternalEmail, isPending: isCancelling } =
    useCancelInternalEmail();
  const { mutateAsync: sendInternalEmailNow, isPending: isSendingNow } =
    useSendInternalEmailNow();
  // Held past the cancel itself: the row only leaves the list once `onDone` has
  // re-read it, and that is fire-and-forget — so until then the row still offered
  // Send now and Edit on an email that was already gone.
  const [deleted, setDeleted] = useState(false);
  const isDeleting = isCancelling || deleted;

  const handleSendNow = async () => {
    try {
      await sendInternalEmailNow(internalEmailId);
      toast({ title: SEND_NOW_SUCCEEDED, status: 'info' });
      onDone();
    } catch {
      toast({ title: SEND_NOW_FAILED, status: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await cancelInternalEmail(internalEmailId);
      setDeleted(true);
      toast({ title: DELETE_SUCCEEDED });
      onDone();
    } catch {
      toast({ title: DELETE_FAILED, status: 'error' });
    }
  };

  // The menu closed on the click, so the trigger's place is the one spot left to
  // say what is happening. The spinner stands where the ⋮ was, at its size, so the
  // row does not shift — and there is no menu to reach, so nothing can act on the
  // email twice.
  if (isDeleting) {
    return (
      <ActivityTooltip label={DELETING_LABEL}>
        <Flex
          align="center"
          justify="center"
          boxSize="32px"
          role="status"
          aria-label={DELETING_LABEL}
          data-test-id="conversation-internal-email-deleting"
        >
          <ActivityBusyIcon size={18} />
        </Flex>
      </ActivityTooltip>
    );
  }

  return (
    <ActivityMoreMenu
      data-test-id="conversation-internal-email-more-menu-button"
      items={[
        {
          id: 'send-now',
          label: isSendingNow ? SENDING_LABEL : SEND_NOW_LABEL,
          icon: isSendingNow ? ActivityBusyIcon : Send01,
          disabled: isSendingNow,
          onClick: handleSendNow,
        },
        {
          id: 'edit',
          label: EDIT_LABEL,
          icon: Edit05,
          color: EDIT_COLOR,
          hidden: !onEdit,
          onClick: () => onEdit?.(),
        },
        {
          id: 'delete',
          label: DELETE_LABEL,
          icon: Trash01,
          color: DELETE_COLOR,
          destructive: true,
          onClick: handleDelete,
        },
      ]}
    />
  );
};
